import { supabase, isConfigured } from './supabase';
import type {
  DashboardKPIs,
  PipelineCount,
  TopClient,
  LeaderboardEntry,
  FollowUpItem,
  UpcomingDemo,
  ActivityFeedItem,
  ContactType,
} from '../types';

export async function getDashboardKPIs(
  teamId: string,
  ownerUserId?: string,
  leadType?: ContactType
): Promise<DashboardKPIs> {
  const defaultKPIs: DashboardKPIs = {
    new_leads_7d: 0,
    demos_booked_7d: 0,
    demos_completed_7d: 0,
    signups_7d: 0,
    revenue_30d_cents: 0,
    commission_30d_cents: 0,
  };

  if (!isConfigured) return defaultKPIs;

  const { data, error } = await supabase.rpc('get_sales_dashboard_kpis', {
    p_team_id: teamId,
    p_owner_user_id: ownerUserId || null,
    p_lead_type: leadType || null,
  });

  if (error) {
    console.error('[dashboard] getDashboardKPIs failed:', error);
    return defaultKPIs;
  }

  if (data && data.length > 0) {
    return {
      new_leads_7d: Number(data[0].new_leads_7d) || 0,
      demos_booked_7d: Number(data[0].demos_booked_7d) || 0,
      demos_completed_7d: Number(data[0].demos_completed_7d) || 0,
      signups_7d: Number(data[0].signups_7d) || 0,
      revenue_30d_cents: Number(data[0].revenue_30d_cents) || 0,
      commission_30d_cents: Number(data[0].commission_30d_cents) || 0,
    };
  }

  return defaultKPIs;
}

export async function getPipelineOverview(
  teamId: string,
  ownerUserId?: string,
  leadType?: ContactType
): Promise<PipelineCount[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase.rpc('get_sales_pipeline_overview', {
    p_team_id: teamId,
    p_owner_user_id: ownerUserId || null,
    p_lead_type: leadType || null,
  });

  if (error) {
    console.error('[dashboard] getPipelineOverview failed:', error);
    return [];
  }

  return (data || []).map((row: { status: string; count: number }) => ({
    status: row.status,
    count: Number(row.count) || 0,
  })) as PipelineCount[];
}

export async function getTopClientsByRevenue(
  teamId: string,
  limit = 10
): Promise<TopClient[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase.rpc('get_top_clients_by_revenue', {
    p_team_id: teamId,
    p_limit: limit,
  });

  if (error) {
    console.error('[dashboard] getTopClientsByRevenue failed:', error);
    return [];
  }

  return (data || []) as TopClient[];
}

export async function getCommissionLeaderboard(
  teamId: string,
  days = 30
): Promise<LeaderboardEntry[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase.rpc('get_commission_leaderboard', {
    p_team_id: teamId,
    p_days: days,
  });

  if (error) {
    console.error('[dashboard] getCommissionLeaderboard failed:', error);
    return [];
  }

  return (data || []) as LeaderboardEntry[];
}

export async function getDashboardFollowups(
  teamId: string,
  ownerUserId?: string,
  limit = 20
): Promise<FollowUpItem[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase.rpc('get_dashboard_followups', {
    p_team_id: teamId,
    p_owner_user_id: ownerUserId || null,
    p_limit: limit,
  });

  if (error) {
    console.error('[dashboard] getDashboardFollowups failed:', error);
    return [];
  }

  return (data || []) as FollowUpItem[];
}

export async function getUpcomingDemos(
  teamId: string,
  limit = 10
): Promise<UpcomingDemo[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase.rpc('get_upcoming_demos', {
    p_team_id: teamId,
    p_limit: limit,
  });

  if (error) {
    console.error('[dashboard] getUpcomingDemos failed:', error);
    return [];
  }

  return (data || []) as UpcomingDemo[];
}

export async function getActivityFeed(
  teamId: string,
  limit = 20
): Promise<ActivityFeedItem[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase.rpc('get_activity_feed', {
    p_team_id: teamId,
    p_limit: limit,
  });

  if (error) {
    console.error('[dashboard] getActivityFeed failed:', error);
    return [];
  }

  return (data || []) as ActivityFeedItem[];
}
